import { fetchJSON, HttpError } from '../utils/fetchClient';
import { shouldSplitBoundingBox, splitBoundingBoxRecursively, DEFAULT_TARGET_TILE_SIZE } from '../utils/boundingBoxSplitter';
import { createLogger } from '../utils/logger';

const log = createLogger('ObservationsAPI');
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

interface Parameter {
  name: string;
  unit: string;
  imperial_unit: string;
  type: string;
  precision: number | null;
  description: string;
  notes: string | null;
  requiredScope: string;
}

interface ObservationResponse {
  type?: string;
  features?: Array<{
    tags?: Record<string, string>;
    properties: {
      [key: string]: any;
    };
  }>;
}

interface StationInfo {
  stationCode: string;
  name?: string;
  tags: Record<string, string>;
}

// Bounding box query interfaces
export interface BoundingBox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

export interface ObservationQueryParams {
  boundingBox: BoundingBox;
  startTime: string;
  endTime: string;
  parameters: string[];
  onlyGood: boolean;
  showTags: boolean;
  obsProperties: boolean;
  /** Observation types to include (default: all major types) */
  obsTypes?: string;
}

// Default observation types to include in queries
export const DEFAULT_OBS_TYPES = 'Hydro,Customer,SYNOP,METAR,HFM,AG,Citizen,RWIS,Others';

export interface ObservationValue {
  value: number;
  unit: string;
  qcStatus?: 'pass' | 'suspicious' | 'fail';
  obsType?: string;
}

export interface ProcessedObservation {
  stationCode: string;
  stationName: string;
  latitude: number;
  longitude: number;
  country?: string;
  timestamp: string;
  values: Map<string, ObservationValue>;
}

/**
 * Options for parallel fetch operations.
 * Implements Requirements 6.2, 6.5, 6.6
 */
export interface ParallelFetchOptions {
  /** Callback for progress updates (completed quadrants, total quadrants) */
  onProgress?: (completed: number, total: number) => void;
  /** Maximum retries per quadrant (default: 3) */
  maxRetries?: number;
  /** Rate limit in requests per second (default: 250) */
  rateLimit?: number;
  /**
   * Callback for progressive loading - called when each tile completes.
   * Allows UI to render data incrementally as it arrives.
   */
  onTileComplete?: (observations: ProcessedObservation[]) => void;
  /**
   * AbortSignal for cancelling in-flight requests.
   * Used to cancel regular fetches when animation mode is entered.
   * Implements Requirements 2.1, 2.2
   */
  signal?: AbortSignal;
}

/**
 * Result of a parallel fetch operation.
 * Implements Requirements 6.2, 6.5, 6.6
 */
export interface ParallelFetchResult {
  /** Merged and deduplicated observations from all quadrants */
  observations: ProcessedObservation[];
  /** Bounding boxes of quadrants that failed after all retries */
  failedQuadrants: BoundingBox[];
  /** Total number of API requests made (including retries) */
  totalRequests: number;
}

// DTN API response types
interface DTNFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number, number?]; // [lon, lat, alt?]
  };
  tags: {
    stationCode: string;
    name?: string;
    country?: string;
    [key: string]: string | undefined;
  };
  properties: {
    [timestamp: string]: {
      [parameter: string]: number | null | {
        qc?: string;
        obsType?: string;
      };
    };
  };
}

interface DTNObservationResponse {
  type: 'FeatureCollection';
  features: DTNFeature[];
}

// ponytail: module-level token cache shared across all ObservationsAPI instances.
// Prevents rate-limiting (429) when multiple components create their own instances.
let _sharedToken: string | null = null;
let _sharedTokenExpiry: number = 0;
let _tokenPromise: Promise<string> | null = null;

export class ObservationsAPI {
  private baseURL: string;

  constructor() {
    // Use current origin in browser, fallback to localhost for SSR/tests
    this.baseURL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';
    log.debug(`Using base URL: ${this.baseURL}`);
  }

  // ponytail: single helper replaces AxiosInstance + interceptor combo
  private async authFetch<T>(path: string, options?: RequestInit & { signal?: AbortSignal }): Promise<T> {
    const token = await this.getAccessToken();
    const url = `${this.baseURL}/api/observations${path}`;
    return fetchJSON<T>(url, {
      ...options,
      headers: { ...options?.headers as Record<string, string>, Authorization: `Bearer ${token}` },
    });
  }

  async getAccessToken(): Promise<string> {
    // Return shared cached token if still valid
    if (_sharedToken && Date.now() < _sharedTokenExpiry) {
      return _sharedToken;
    }

    // If another call is already fetching, wait for it (deduplication)
    if (_tokenPromise) {
      return _tokenPromise;
    }

    // Fetch new token (singleton promise prevents parallel requests)
    _tokenPromise = this._fetchToken();
    try {
      return await _tokenPromise;
    } finally {
      _tokenPromise = null;
    }
  }

  private async _fetchToken(): Promise<string> {
    try {
      const response = await fetchJSON<{ access_token: string; expires_in?: number }>(
        `${this.baseURL}/api/auth/token?service=observations`,
        { method: 'POST' }
      );

      _sharedToken = response.access_token;
      const expiresIn = response.expires_in || 3600;
      _sharedTokenExpiry = Date.now() + (expiresIn - 300) * 1000;

      if (!_sharedToken) {
        throw new Error('No access token received');
      }

      return _sharedToken;
    } catch (error) {
      log.error('Failed to get observations access token:', error);
      throw new Error('Failed to authenticate with observations API');
    }
  }

  async getParameters(): Promise<Parameter[]> {
    try {
      return await this.authFetch<Parameter[]>('/parameters');
    } catch (error) {
      log.error('Failed to fetch parameters:', error);
      throw new Error('Failed to fetch observation parameters');
    }
  }

  async getObservations(
    stationCode: string,
    startTime: string,
    endTime: string,
    parameters: string[],
    obsTypes?: string,
    units?: string
  ): Promise<{
    observations: Array<{ timestamp: string; [key: string]: string | number | null }>;
    stationInfo?: StationInfo;
  }> {
    const maxRetries = 5;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        log.debug(`Attempt ${attempt}/${maxRetries} to fetch observations...`);
        
        return await this.fetchObservationsAttempt(stationCode, startTime, endTime, parameters, obsTypes, units);
      } catch (error: any) {
        lastError = error;
        log.error(`Attempt ${attempt}/${maxRetries} failed:`, error.message);
        
        // Don't retry on auth/not-found, or on 429: a rate-limit response means "send
        // fewer requests", so retrying (5×, and fanned out across map views) only deepens
        // the throttle. Fail fast and let the caller honor Retry-After.
        if (error instanceof HttpError && (error.status === 401 || error.status === 403 || error.status === 404 || error.status === 429)) {
          throw error;
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          log.error('All retry attempts exhausted');
          throw error;
        }
        
        // Wait before retrying (exponential backoff: 2s, 4s, 8s, 16s)
        const waitTime = Math.pow(2, attempt) * 1000;
        log.debug(`Waiting ${waitTime / 1000}s before retry...`);
        await sleep(waitTime);
      }
    }
    
    throw lastError;
  }

  private async fetchObservationsAttempt(
    stationCode: string,
    startTime: string,
    endTime: string,
    parameters: string[],
    obsTypes?: string,
    units?: string
  ): Promise<{
    observations: Array<{ timestamp: string; [key: string]: string | number | null }>;
    stationInfo?: StationInfo;
  }> {
    try {
      const params = new URLSearchParams({
        startTime,
        endTime,
        obsProperties: 'true',
        parameters: parameters.join(','),
        stationCode,
        showTags: 'true',
        onlyGood: 'false',
      });
      
      if (obsTypes) {
        params.append('obsTypes', obsTypes);
      }
      
      if (units) {
        params.append('units', units);
      }

      const data = await this.authFetch<ObservationResponse>(`?${params.toString()}`);

      log.debug('Full API Response:', data);
      log.debug('Observations response type:', data.type);
      
      // The API returns a GeoJSON FeatureCollection
      if (data.type === 'FeatureCollection' && data.features) {
        // Extract station info from the first feature
        let stationInfo: StationInfo | undefined;
        if (data.features.length > 0) {
          const firstFeature = data.features[0];
          log.debug('First Feature:', firstFeature);
          
          // Tags are at the feature level, not in properties!
          const tags = (firstFeature as any).tags || {};
          log.debug('Feature.tags:', tags);
          
          stationInfo = {
            stationCode: tags.stationCode || stationCode,
            name: tags.name,
            tags: tags
          };
          
          log.debug('Final extracted stationInfo:', stationInfo);
        }
        
        // Extract observations from GeoJSON features
        const observations = data.features.flatMap((feature: any) => {
          const properties = feature.properties || {};
          // Task 6: Add type guard before .match() to prevent TypeError (Requirement 5.1)
          const timestamps = Object.keys(properties).filter(key => {
            if (typeof key !== 'string') {
              log.warn(`Property key is not a string: ${typeof key}`, key);
              return false;
            }
            return key.match(/^\d{4}-\d{2}-\d{2}T/);
          });
          
          return timestamps.map(timestamp => {
            const obs = properties[timestamp];
            const cleanObs: any = { timestamp };
            
            Object.keys(obs).forEach(key => {
              if (key.endsWith('Properties')) {
                const paramName = key.replace('Properties', '');
                cleanObs[`${paramName}_qc`] = obs[key]?.qc;
                cleanObs[`${paramName}_obsType`] = obs[key]?.obsType;
              } else {
                cleanObs[key] = obs[key];
              }
            });
            
            return cleanObs;
          });
        });
        
        log.debug('Parsed observations:', observations.length);
        log.debug('Station info:', stationInfo);
        return { observations, stationInfo };
      }
      
      return { observations: [] };
    } catch (error: any) {
      log.error('Failed to fetch observations:', error);
      log.error('Error details:', { message: error.message, status: error instanceof HttpError ? error.status : undefined });
      
      // Provide more specific error messages
      if (error instanceof HttpError) {
        if (error.status === 429) {
          throw error; // ponytail: re-throw so callers can detect rate-limit and fail fast
        }
        if (error.status === 401 || error.status === 403) {
          throw new Error('Authentication failed. Please check your API credentials.');
        } else if (error.status === 404) {
          throw new Error('Station not found or no observations available.');
        }
      }
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        throw new Error('Request timeout. The server took too long to respond.');
      }
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check if the proxy server is running on http://localhost:3001');
      }
      
      throw new Error(`Failed to fetch observations: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Fetch observations within a square box (± boxKm) around a center point.
   *
   * The observations API has no radius/location mode — only by=boundingBox — so we
   * derive a lat/lon box around the origin and delegate to getObservationsByBoundingBox.
   * Used by the QC panel's Nearby Stations Map (spatial comparison). Returns
   * ProcessedObservation[] directly (no adaptation needed — the map consumes it as-is).
   *
   * boxKm is the half-extent in km. 1° latitude ≈ 111 km; longitude degrees shrink by
   * cos(latitude), clamped so we don't blow up near the poles.
   */
  async getObservationsByBox(
    centerLat: number,
    centerLon: number,
    boxKm: number,
    startTime: string,
    endTime: string,
    parameters: string[],
    obsTypes?: string,
    aspect = 1,
  ): Promise<ProcessedObservation[]> {
    // boxKm is the N-S half-extent (km). `aspect` (map width/height) stretches the E-W
    // half-extent so the queried rectangle matches the visible map — no visible area is
    // left un-queried (and vice-versa).
    const latDelta = boxKm / 111;
    const lonDelta = (boxKm * Math.max(aspect, 1)) / (111 * Math.max(Math.cos((centerLat * Math.PI) / 180), 0.01));
    const boundingBox = {
      minLat: Math.max(-90, centerLat - latDelta),
      maxLat: Math.min(90, centerLat + latDelta),
      minLon: Math.max(-180, centerLon - lonDelta),
      maxLon: Math.min(180, centerLon + lonDelta),
    };
    return this.getObservationsByBoundingBox({
      boundingBox,
      startTime,
      endTime,
      parameters,
      onlyGood: false,
      showTags: true,
      obsProperties: true,
      obsTypes,
    });
  }

  /**
   * Fetch observations by bounding box for map-based visualization.
   * Implements Requirements 7.2 and 7.3.
   */
  async getObservationsByBoundingBox(params: ObservationQueryParams): Promise<ProcessedObservation[]> {
    const maxRetries = 5;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        log.debug(`Bounding box query attempt ${attempt}/${maxRetries}...`);
        return await this.fetchBoundingBoxObservationsAttempt(params);
      } catch (error: any) {
        lastError = error;
        log.error(`Bounding box attempt ${attempt}/${maxRetries} failed:`, error.message);
        
        // Don't retry on auth/not-found, or on 429: a rate-limit response means "send
        // fewer requests", so retrying (5×, and fanned out across map views) only deepens
        // the throttle. Fail fast and let the caller honor Retry-After.
        if (error instanceof HttpError && (error.status === 401 || error.status === 403 || error.status === 404 || error.status === 429)) {
          throw error;
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          log.error('All retry attempts exhausted for bounding box query');
          throw error;
        }
        
        // Wait before retrying (exponential backoff: 2s, 4s, 8s, 16s)
        const waitTime = Math.pow(2, attempt) * 1000;
        log.debug(`Waiting ${waitTime / 1000}s before retry...`);
        await sleep(waitTime);
      }
    }
    
    throw lastError;
  }

  private async fetchBoundingBoxObservationsAttempt(
    params: ObservationQueryParams,
    signal?: AbortSignal
  ): Promise<ProcessedObservation[]> {
    try {
      const { boundingBox, startTime, endTime, parameters, onlyGood, showTags, obsProperties, obsTypes } = params;
      
      const queryParams = new URLSearchParams({
        by: 'boundingBox',
        minLat: boundingBox.minLat.toString(),
        maxLat: boundingBox.maxLat.toString(),
        minLon: boundingBox.minLon.toString(),
        maxLon: boundingBox.maxLon.toString(),
        startTime,
        endTime,
        parameters: parameters.join(','),
        showTags: showTags.toString(),
        obsProperties: obsProperties.toString(),
        onlyGood: onlyGood.toString(),
        obsType: obsTypes || DEFAULT_OBS_TYPES,
      });

      log.debug(`Fetching observations with bounding box query: ${queryParams.toString()}`);

      const data = await this.authFetch<DTNObservationResponse>(`?${queryParams.toString()}`, { signal });

      log.debug('Bounding box API Response:', data);
      log.debug('Response type:', data.type);
      log.debug('Features count:', data.features?.length || 0);

      return this.parseFeatureCollectionResponse(data);
    } catch (error: any) {
      // Handle abort errors gracefully (Requirement 2.3)
      if (error.name === 'AbortError') {
        log.debug('Request cancelled');
        throw error;
      }
      
      log.error('Failed to fetch bounding box observations:', error);
      log.error('Error details:', { message: error.message, status: error instanceof HttpError ? error.status : undefined });
      
      if (error instanceof HttpError) {
        if (error.status === 429) {
          throw error; // ponytail: re-throw so callers can detect rate-limit and fail fast
        }
        if (error.status === 401 || error.status === 403) {
          throw new Error('Authentication failed. Please check your API credentials.');
        } else if (error.status === 404) {
          throw new Error('No observations available for the specified region.');
        }
      }
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        throw new Error('Request timeout. The server took too long to respond.');
      }
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check if the proxy server is running on http://localhost:3001');
      }
      
      throw new Error(`Failed to fetch observations: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Parse DTN GeoJSON FeatureCollection response into ProcessedObservation array.
   */
  private parseFeatureCollectionResponse(response: DTNObservationResponse): ProcessedObservation[] {
    if (!response) {
      log.debug('Empty response, returning empty array');
      return [];
    }

    if (response.type !== 'FeatureCollection') {
      log.debug('Response is not a FeatureCollection (likely empty region):', response.type || 'no type');
      return [];
    }

    if (!response.features || response.features.length === 0) {
      log.debug('FeatureCollection has no features');
      return [];
    }

    const observations: ProcessedObservation[] = [];

    for (const feature of response.features) {
      const [longitude, latitude] = feature.geometry.coordinates;
      const stationCode = feature.tags?.stationCode || 'unknown';
      const stationName = feature.tags?.name || stationCode;
      const country = feature.tags?.country;

      const properties = feature.properties || {};
      const timestamps = Object.keys(properties).filter(key => {
        if (typeof key !== 'string') return false;
        return key.match(/^\d{4}-\d{2}-\d{2}T/);
      });

      for (const timestamp of timestamps) {
        const obsData = properties[timestamp];
        if (!obsData || typeof obsData !== 'object') continue;

        const values = new Map<string, ObservationValue>();

        for (const [key, value] of Object.entries(obsData)) {
          if (key.endsWith('Properties')) continue;
          if (value === null || value === undefined) continue;

          const propsKey = `${key}Properties`;
          const paramProps = obsData[propsKey] as { qc?: string; obsType?: string } | undefined;

          let qcStatus: 'pass' | 'suspicious' | 'fail' | undefined;
          if (paramProps?.qc) {
            const qcLower = paramProps.qc.toLowerCase();
            if (qcLower === 'pass' || qcLower === 'passed') {
              qcStatus = 'pass';
            } else if (qcLower === 'suspicious') {
              qcStatus = 'suspicious';
            } else if (qcLower === 'fail' || qcLower === 'failed') {
              qcStatus = 'fail';
            }
          }

          values.set(key, {
            value: value as number,
            unit: '',
            qcStatus,
            obsType: paramProps?.obsType,
          });
        }

        if (values.size > 0) {
          observations.push({ stationCode, stationName, latitude, longitude, country, timestamp, values });
        }
      }
    }

    log.debug(`Parsed ${observations.length} observations from ${response.features.length} features`);
    return observations;
  }

  /**
   * Merge observations from multiple quadrants and deduplicate by station code + timestamp.
   * Multiple data sources for the same station are PRESERVED (not merged here) so that
   * downstream obsType filtering can select which sources to average. Deduplication only
   * removes exact duplicates from overlapping tile fetches (same station+timestamp+values).
   * Implements Requirements 6.3, 6.4
   */
  mergeAndDeduplicate(observationSets: ProcessedObservation[][]): ProcessedObservation[] {
    const allObservations = observationSets.flat();
    // ponytail: dedup key includes obsType of the first parameter so multiple sources
    // for the same station+timestamp are kept as separate entries. Tile overlaps (exact
    // duplicates) are still removed. Ceiling: if a source reports different obsTypes per
    // parameter within one observation, this may keep a duplicate — acceptable edge case.
    const seen = new Map<string, ProcessedObservation>();

    for (const observation of allObservations) {
      // Derive the obsType from the first value that has one (or '' if none)
      let obsType = '';
      for (const v of observation.values.values()) {
        if (v.obsType) { obsType = v.obsType; break; }
      }
      const key = `${observation.stationCode}|${observation.timestamp}|${obsType}`;
      if (!seen.has(key)) {
        seen.set(key, observation);
      }
    }

    return Array.from(seen.values());
  }

  /**
   * Execute requests with rate limiting.
   * Implements Requirement 6.7.
   */
  private async executeWithRateLimit(
    quadrants: BoundingBox[],
    params: Omit<ObservationQueryParams, 'boundingBox'>,
    options?: ParallelFetchOptions
  ): Promise<{ results: ProcessedObservation[][]; errorIndices: number[] }> {
    // ponytail: was 16 — reduced to avoid tripping the server's 60 req/min
    // observations limiter. 4 concurrent requests × up to 4 batches = 16 tiles still served
    // within a single window. Ceiling: bump to 8 if DTN upstream proves it can handle it.
    const concurrencyLimit = 4;
    const results: ProcessedObservation[][] = new Array(quadrants.length).fill([]);
    const errorIndices: number[] = [];
    let completedCount = 0;

    if (options?.signal?.aborted) {
      log.debug('Request already aborted, skipping batch execution');
      return { results, errorIndices };
    }

    for (let batchStart = 0; batchStart < quadrants.length; batchStart += concurrencyLimit) {
      if (options?.signal?.aborted) {
        log.debug('Request aborted, stopping batch execution');
        break;
      }

      const batchEnd = Math.min(batchStart + concurrencyLimit, quadrants.length);
      const batch = quadrants.slice(batchStart, batchEnd);
      
      const batchPromises = batch.map(async (quadrant, batchIndex) => {
        const globalIndex = batchStart + batchIndex;
        const queryParams: ObservationQueryParams = { ...params, boundingBox: quadrant };

        try {
          const observations = await this.fetchBoundingBoxObservationsAttempt(queryParams, options?.signal);
          results[globalIndex] = observations;
          
          if (options?.onTileComplete && observations.length > 0) {
            options.onTileComplete(observations);
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            log.debug(`Request cancelled for quadrant ${globalIndex}`);
            results[globalIndex] = [];
            return;
          }
          log.error(`Request failed for quadrant ${globalIndex}:`, error);
          results[globalIndex] = [];
          errorIndices.push(globalIndex);
        }
      });

      await Promise.all(batchPromises);

      // ponytail: 200ms pause between batches to stay under rate limits
      if (batchEnd < quadrants.length) {
        await sleep(200);
      }
      
      completedCount += batch.length;
      if (options?.onProgress) {
        options.onProgress(completedCount, quadrants.length);
      }
    }

    return { results, errorIndices };
  }

  /**
   * Fetch observations with parallel requests for large viewports.
   * Implements Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8
   */
  async getObservationsParallel(
    params: ObservationQueryParams,
    options?: ParallelFetchOptions
  ): Promise<ParallelFetchResult> {
    const maxRetries = options?.maxRetries ?? 3;
    let totalRequests = 0;

    // Check if bounding box should be split (Requirement 6.8)
    if (!shouldSplitBoundingBox(params.boundingBox)) {
      log.debug('Small viewport, using single request');
      totalRequests = 1;

      try {
        const observations = await this.fetchBoundingBoxObservationsAttempt(params, options?.signal);
        
        if (options?.onTileComplete && observations.length > 0) {
          options.onTileComplete(observations);
        }
        if (options?.onProgress) {
          options.onProgress(1, 1);
        }

        return { observations, failedQuadrants: [], totalRequests };
      } catch (error: any) {
        if (error.name === 'AbortError') {
          log.debug('Single request cancelled');
          return { observations: [], failedQuadrants: [], totalRequests };
        }
        log.error('Single request failed:', error);
        return { observations: [], failedQuadrants: [params.boundingBox], totalRequests };
      }
    }

    // Large viewport: recursively split into tiles
    log.debug('Large viewport, splitting into tiles recursively');
    const tiles = splitBoundingBoxRecursively(params.boundingBox, DEFAULT_TARGET_TILE_SIZE);
    const totalTiles = tiles.length;

    const { boundingBox: _, ...baseParams } = params;

    interface TileState {
      tile: BoundingBox;
      observations: ProcessedObservation[];
      failed: boolean;
      retryCount: number;
    }

    const tileStates: TileState[] = tiles.map(tile => ({
      tile, observations: [], failed: false, retryCount: 0,
    }));

    // Initial fetch for all tiles
    log.debug(`Making initial parallel requests for ${totalTiles} tiles`);
    const { results: initialResults, errorIndices } = await this.executeWithRateLimit(tiles, baseParams, {
      ...options,
      onProgress: undefined,
    });
    totalRequests += totalTiles;

    let completedCount = 0;
    for (let i = 0; i < tileStates.length; i++) {
      if (errorIndices.includes(i)) {
        tileStates[i].failed = true;
      } else {
        tileStates[i].observations = initialResults[i];
        completedCount++;
      }
    }

    if (options?.onProgress) {
      options.onProgress(completedCount, totalTiles);
    }

    // Retry failed tiles (Requirement 6.5)
    let retryRound = 0;
    while (retryRound < maxRetries) {
      if (options?.signal?.aborted) {
        log.debug('Request aborted, stopping retry rounds');
        break;
      }

      const failedTileIndices = tileStates
        .map((state, index) => ({ state, index }))
        .filter(({ state }) => state.failed && state.retryCount < maxRetries)
        .map(({ index }) => index);

      if (failedTileIndices.length === 0) break;

      retryRound++;
      log.debug(`Retry round ${retryRound}/${maxRetries} for ${failedTileIndices.length} failed tiles`);

      const retryPromises = failedTileIndices.map(async (index) => {
        const state = tileStates[index];
        state.retryCount++;
        totalRequests++;

        try {
          const queryParams: ObservationQueryParams = { ...baseParams, boundingBox: state.tile };
          const observations = await this.fetchBoundingBoxObservationsAttempt(queryParams, options?.signal);
          
          state.observations = observations;
          state.failed = false;
          completedCount++;
          
          if (options?.onTileComplete && observations.length > 0) {
            options.onTileComplete(observations);
          }
          
          log.debug(`Retry successful for tile ${index} (${observations.length} observations)`);
        } catch (error: any) {
          if (error.name === 'AbortError') {
            log.debug(`Retry cancelled for tile ${index}`);
            state.failed = false;
            return;
          }
          log.error(`Retry ${state.retryCount}/${maxRetries} failed for tile ${index}:`, error);
        }
      });

      await Promise.all(retryPromises);

      if (options?.onProgress) {
        options.onProgress(completedCount, totalTiles);
      }
    }

    // Collect results
    const successfulObservations: ProcessedObservation[][] = [];
    const failedQuadrants: BoundingBox[] = [];

    for (const state of tileStates) {
      if (state.failed) {
        failedQuadrants.push(state.tile);
      } else {
        successfulObservations.push(state.observations);
      }
    }

    const mergedObservations = this.mergeAndDeduplicate(successfulObservations);

    log.info(`Parallel fetch complete: ${mergedObservations.length} observations, ${failedQuadrants.length} failed tiles, ${totalRequests} total requests`);

    return { observations: mergedObservations, failedQuadrants, totalRequests };
  }

  /**
   * Fetches observation data for a specific tile and time chunk.
   * Used by the chunked animation loading system.
   * **Validates: Requirement 4.3**
   */
  async fetchTileChunk(
    tile: { id: string; boundingBox: BoundingBox },
    startTime: string,
    endTime: string,
    parameter: string
  ): Promise<string> {
    const params: ObservationQueryParams = {
      boundingBox: tile.boundingBox,
      startTime,
      endTime,
      parameters: [parameter],
      onlyGood: false,
      showTags: true,
      obsProperties: true,
    };

    try {
      const queryParams = new URLSearchParams({
        by: 'boundingBox',
        minLat: params.boundingBox.minLat.toString(),
        maxLat: params.boundingBox.maxLat.toString(),
        minLon: params.boundingBox.minLon.toString(),
        maxLon: params.boundingBox.maxLon.toString(),
        startTime: params.startTime,
        endTime: params.endTime,
        parameters: params.parameters.join(','),
        showTags: params.showTags.toString(),
        obsProperties: params.obsProperties.toString(),
        onlyGood: params.onlyGood.toString(),
        obsType: DEFAULT_OBS_TYPES,
      });

      log.debug(`Fetching tile chunk: ${tile.id} [${startTime} - ${endTime}]`);

      // ponytail: 60s timeout via AbortController, replaces axios timeout option
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      try {
        const data = await this.authFetch<DTNObservationResponse>(`?${queryParams.toString()}`, { signal: controller.signal });
        return JSON.stringify(data);
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error: any) {
      log.error(`Failed to fetch tile chunk ${tile.id}:`, error.message);

      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        throw new Error(`Timeout fetching tile ${tile.id}`);
      }

      throw new Error(`Failed to fetch tile ${tile.id}: ${error.message || 'Unknown error'}`);
    }
  }
}
